import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useParams } from "react-router-dom";

import { PageHeader } from "@/components/PageHeader/PageHeader";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { SelectField } from "@/components/SelectField/SelectField";
import { PermissionRoles } from "@/enum";
import { ROUTE_CONFIG } from "@/routes/config";
import { useHasPermissions } from "@/utils/useHasPermissions";

import { useWebhookSigningDialogs } from "../components/signing/useWebhookSigningDialogs";
import { DeliveryDates } from "./DeliveryDates";
import { DeliveryTable } from "./DeliveryTable";
import { DeliveryDetailDialog } from "./DeliveryDetailDialog";
import { RedeliveryDialog } from "./RedeliveryDialog";
import {
	DeliveryFilters,
	EMPTY_FILTERS,
	deliveryQuery,
	redeliveryResult,
} from "./deliveryPresentation";
import { useDeliveryPage, useDeliveryWebhook, useRedeliveryCounts } from "./useDeliveryData";

import type {
	RedeliveryCounts,
	RedeliveryRequest,
	WebhookDelivery,
} from "../schema/webhookDelivery";

export function WebhookDeliveriesRoute() {
	const { webhookId } = useParams();
	const { hasPermissions } = useHasPermissions();
	const id = Number(webhookId);

	if (!hasPermissions([PermissionRoles.WEBHOOK]) || !Number.isSafeInteger(id) || id <= 0) {
		return <Navigate replace to={ROUTE_CONFIG.INTEGRATIONS} />;
	}

	return <WebhookDeliveries key={id} webhookId={id} />;
}

export function WebhookDeliveries({ webhookId }: { webhookId: number }) {
	const { t } = useTranslation("translation", { keyPrefix: "webhookHealth.deliveries" });
	const [filters, setFilters] = useState<DeliveryFilters>(EMPTY_FILTERS);
	const [page, setPage] = useState(1);
	const [take, setTake] = useState(20);
	const [detailId, setDetailId] = useState<number | null>(null);
	const [selection, setSelection] = useState<{
		request: RedeliveryRequest;
		row?: WebhookDelivery;
	} | null>(null);
	const [banner, setBanner] = useState<{ error: boolean; text: string } | null>(null);
	const {
		data: webhook,
		isError: webhookError,
		refetch: refetchWebhook,
	} = useDeliveryWebhook(webhookId);
	const query = useMemo(
		() => deliveryQuery(webhookId, page, take, filters),
		[webhookId, page, take, filters]
	);
	const validRange = !filters.from || !filters.to || filters.from <= filters.to;
	const { data, isFetching, isError, refetch } = useDeliveryPage(query, !validRange);
	const failedQuery = useMemo(() => ({ webhookId, mode: "failed" as const }), [webhookId]);
	const { data: failedCounts } = useRedeliveryCounts(failedQuery);
	const signing = useWebhookSigningDialogs();
	const updateFilters = (next: DeliveryFilters) => {
		setFilters(next);
		setPage(1);
	};
	const openOne = (row: WebhookDelivery) => {
		setDetailId(null);
		setSelection({ request: { mode: "one", deliveryId: row.id }, row });
	};
	const finish = (result: RedeliveryCounts) => {
		setSelection(null);
		setBanner({ error: false, text: redeliveryResult(result, t) });
	};
	const filtered = Boolean(filters.status || filters.from || filters.to);
	const busy = isFetching || !data;

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("pageTitle", { name: webhook?.name ?? "" })} />
			<nav aria-label="Breadcrumb" className="text-sm text-text-meta">
				<Link className="cursor-pointer hover:underline" to={ROUTE_CONFIG.INTEGRATIONS}>
					{t("integrations")}
				</Link>{" "}
				→{" "}
				<Link className="cursor-pointer hover:underline" to={ROUTE_CONFIG.INTEGRATIONS_WEBHOOKS}>
					{t("webhooks")}
				</Link>{" "}
				→ {webhook?.name} → {t("title")}
			</nav>
			<div className="flex flex-wrap items-center justify-between gap-3">
				<p className="break-all font-mono text-xs text-text-meta">{webhook?.url}</p>
				{webhook && (
					<div className="flex flex-wrap gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							size={ButtonSize.S}
							title={t("range")}
							onClick={() => setSelection({ request: { mode: "range" } })}
						/>
						<Button
							color={ButtonColor.ACTION}
							size={ButtonSize.S}
							title={t("failed", {
								count: failedCounts?.resend ?? webhook.givenUpDeliveries ?? 0,
							})}
							onClick={() => setSelection({ request: { mode: "failed" } })}
						/>
					</div>
				)}
			</div>
			<p className="rounded-lg border border-border-light bg-background-light p-3 text-sm text-text">
				{t("retention")}
			</p>
			{banner && (
				<div
					className={`rounded-lg border p-3 text-sm ${banner.error ? "border-status-error bg-status-error-light text-status-error" : "border-status-success bg-status-success-light text-status-success"}`}
					role={banner.error ? "alert" : "status"}
				>
					{banner.text}
				</div>
			)}
			<div className="flex flex-wrap items-end gap-3">
				<div className="w-44">
					<SelectField
						handleSelect={(status) =>
							updateFilters({ ...filters, status: status as DeliveryFilters["status"] })
						}
						label={t("status")}
						options={[
							{ title: t("all"), value: "" },
							...(["DELIVERED", "RETRYING", "GIVEN_UP"] as const).map((value) => ({
								title: t(value),
								value,
							})),
						]}
						portal
						selectedOption={filters.status}
						testId="delivery-status-filter"
					/>
				</div>
				<DeliveryDates
					from={filters.from}
					to={filters.to}
					onChange={(dates) => updateFilters({ ...filters, ...dates })}
				/>
				<Button
					color={ButtonColor.ACTION_SECONDARY}
					size={ButtonSize.S}
					title={t("reset")}
					onClick={() => updateFilters(EMPTY_FILTERS)}
				/>
			</div>
			{!validRange ? (
				<p role="alert">{t("invalidRange")}</p>
			) : isError || webhookError ? (
				<div role="alert">
					{t("loadFailed")}{" "}
					<button
						className="cursor-pointer text-brand-light"
						type="button"
						onClick={() => {
							refetch();
							if (webhookError) {
								refetchWebhook();
							}
						}}
					>
						{t("retry")}
					</button>
				</div>
			) : busy ? (
				<p role="status">{t("loading")}</p>
			) : data.data.length === 0 ? (
				<div className="flex flex-col items-center gap-4 py-16 text-center">
					<h2 className="font-semibold text-text">{t(filtered ? "emptyFiltered" : "empty")}</h2>
					{!filtered && <p className="max-w-md text-sm text-text-meta">{t("emptyDescription")}</p>}
					{filtered ? (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							title={t("reset")}
							onClick={() => updateFilters(EMPTY_FILTERS)}
						/>
					) : (
						webhook && (
							<Button
								color={ButtonColor.ACTION}
								title={t("test")}
								onClick={() => signing.actions.sendTestEvent(webhook)}
							/>
						)
					)}
				</div>
			) : (
				<>
					<DeliveryTable rows={data.data} onDetail={setDetailId} onRedeliver={openOne} />
					<div className="flex flex-wrap items-center justify-between gap-3 text-xs text-text-meta">
						<span>
							{t("showing", {
								from: (page - 1) * take + 1,
								to: Math.min(page * take, data.meta.itemCount),
								count: data.meta.itemCount,
							})}
						</span>
						<div className="flex items-center gap-3">
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={!data.meta.hasPreviousPage}
								size={ButtonSize.XS}
								title={t("previous")}
								onClick={() => setPage(page - 1)}
							/>
							<Button
								color={ButtonColor.ACTION_SECONDARY}
								disabled={!data.meta.hasNextPage}
								size={ButtonSize.XS}
								title={t("next")}
								onClick={() => setPage(page + 1)}
							/>
							<SelectField
								handleSelect={(value) => {
									setTake(Number(value));
									setPage(1);
								}}
								label={t("rows")}
								options={[20, 50, 100].map((value) => ({ title: value, value }))}
								portal
								selectedOption={take}
								testId="delivery-page-size"
							/>
						</div>
					</div>
				</>
			)}
			{detailId !== null && (
				<DeliveryDetailDialog
					key={detailId}
					deliveryId={detailId}
					webhookId={webhookId}
					onClose={() => setDetailId(null)}
					onDetail={setDetailId}
					onRedeliver={openOne}
				/>
			)}
			{selection && webhook && (
				<RedeliveryDialog
					dates={{ from: filters.from, to: filters.to }}
					row={selection.row}
					selection={selection.request}
					webhook={webhook}
					onClose={() => setSelection(null)}
					onError={(text) => {
						setSelection(null);
						setBanner({ error: true, text });
					}}
					onResult={finish}
				/>
			)}
			{signing.dialogs}
		</div>
	);
}
